import { Copy, LockKey } from "@phosphor-icons/react";
import { Button, Dialog, Flex, Text, TextArea } from "@radix-ui/themes";
import { useCallback, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

interface PasswordProtectedPdfDialogProps {
  /** The protected PDF filename to render in the message. Null hides the dialog. */
  fileName: string | null;
  /** Called when the user dismisses the dialog. */
  onClose: () => void;
}

/**
 * Modal shown when a user tries to upload a password-protected PDF.
 * Surfaces a clear, actionable message + a copy-to-clipboard email
 * template the marketing team can send to their agency to request an
 * unrestricted version. Customer-driven: Nestle CSV #4 — Diana asked
 * us to "tell marketing teams up front" rather than failing silently
 * in the review pipeline.
 */
export function PasswordProtectedPdfDialog({
  fileName,
  onClose,
}: PasswordProtectedPdfDialogProps) {
  const { t } = useTranslation("ui");
  const open = fileName != null;
  const [copied, setCopied] = useState(false);
  const copyResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = useCallback(async () => {
    if (!fileName) return;
    const text = t("passwordProtectedPdfDialog.emailTemplate", { fileName });
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for environments without the modern clipboard API
        // (older browsers, some sandbox iframes). Silently no-op rather
        // than throw — the textarea below is selectable as a last resort.
      }
      setCopied(true);
      if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
      copyResetTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Same fallback rationale — keep the dialog usable.
    }
  }, [fileName, t]);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          setCopied(false);
          onClose();
        }
      }}
    >
      <Dialog.Content
        maxWidth="520px"
        data-testid="password-protected-pdf-dialog"
        aria-labelledby="password-protected-pdf-title"
      >
        <Flex align="center" gap="2" mb="2">
          <LockKey size={20} aria-hidden="true" />
          <Dialog.Title id="password-protected-pdf-title" mb="0">
            {t("passwordProtectedPdfDialog.title")}
          </Dialog.Title>
        </Flex>
        <Dialog.Description size="2" mb="3">
          <Trans
            i18nKey="passwordProtectedPdfDialog.description"
            ns="ui"
            values={{
              fileName:
                fileName ?? t("passwordProtectedPdfDialog.fallbackFileName"),
            }}
            components={{ strong: <strong /> }}
          />
        </Dialog.Description>
        <Text
          as="label"
          size="2"
          weight="medium"
          htmlFor="password-protected-pdf-template"
        >
          {t("passwordProtectedPdfDialog.suggestedMessageLabel")}
        </Text>
        <TextArea
          id="password-protected-pdf-template"
          data-testid="password-protected-pdf-template"
          value={
            fileName
              ? t("passwordProtectedPdfDialog.emailTemplate", { fileName })
              : ""
          }
          readOnly
          rows={8}
          style={{ marginTop: 6 }}
          aria-label={t("passwordProtectedPdfDialog.templateAriaLabel")}
        />
        <Flex mt="3" gap="2" justify="end">
          <Button
            data-testid="password-protected-pdf-copy"
            variant="solid"
            onClick={handleCopy}
            aria-label={t("passwordProtectedPdfDialog.copyAriaLabel")}
          >
            <Copy size={16} aria-hidden="true" />
            {copied
              ? t("passwordProtectedPdfDialog.copiedLabel")
              : t("passwordProtectedPdfDialog.copyLabel")}
          </Button>
          <Button
            data-testid="password-protected-pdf-close"
            variant="soft"
            color="gray"
            onClick={onClose}
          >
            {t("passwordProtectedPdfDialog.closeLabel")}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
